import { useMsal } from '@azure/msal-react';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, makeStyles, mergeClasses, SearchBox, Tab, TabList, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { downloadFile } from '../../common/file/download-file';
import { useCommonStyles, useStores } from '../../common/hooks';
import { QuestionnaireMediaType } from '../../common/types/questionnaire-media-type';
import { AwardGroupRef, blankCountry, blankQuestionnaire, Country, Questionnaire, WinnerQuestionnaire } from '../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../models/resource-services';
import { PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { AwardGroupList } from './components/AwardGroupList';
import { WinnerDetails } from './components/WinnerDetails';
import { WinnersAndFinalistPageTabs } from './winners-and-finalists-page-ui-store';
import './WinnersAndFinalistPage.scss';

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
  },
});

const WinnersAndFinalistsPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    apiEndpointCallStatesStore,
    awardStore,
    countryStore,
    winnersAndFinalistsPageUiStore
  } = useStores();

  const commonStyles = useCommonStyles();
  const classes = useStyles();

  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<WinnersAndFinalistPageTabs>('');

  const isDownloadServiceInFlight = apiEndpointCallStatesStore.inFlightStatusMap.get('postResourceTicket') ?? false;

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const awardGroups = await apiStore.getAwardGroups(instance, { searchText: query });
        winnersAndFinalistsPageUiStore.setAwardGroups(awardGroups);
      }
      finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const handleSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleAwardSelected = async (award: AwardGroupRef): Promise<void> => {
    winnersAndFinalistsPageUiStore.setSelectedAward(award);
    setActiveTab('Winner');
    setIsLoading(true);
    try {
      const winners = await apiStore.getAwardWinners(instance, award.awardId);
      winnersAndFinalistsPageUiStore.setWinners(winners);
      const selectedWinner = winners.find((winner) => winner.role === 'Winner');
      winnersAndFinalistsPageUiStore.setWinner(selectedWinner);
      await loadQuestionnaire();
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountrySelected = async (country: Country): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    winnersAndFinalistsPageUiStore.setSelectedCountry(country);
    setActiveTab('Winner');
    setIsLoading(true);
    try {
      const winners = await apiStore.getCountryAwardWinners(instance, awardStore.defaultProgramRef.programId, country.countryId);
      winnersAndFinalistsPageUiStore.setWinners(winners);
      const selectedWinner = winners.find((winner) => winner.role === 'Winner');
      winnersAndFinalistsPageUiStore.setWinner(selectedWinner);
      await loadQuestionnaire();
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEditedQuestionnaire = async (questionnaireId: number): Promise<void> => {
    if (!winnersAndFinalistsPageUiStore.selectedWinner) return;
    const questionnaire = winnersAndFinalistsPageUiStore.getWinnerQuestionnaire(questionnaireId);
    if (questionnaire.questionnaireId === 0) return;

    setIsLoading(true);

    const celebratoryItemAddress = questionnaire.contact.addresses!.find(address => address.addressTypeCode === 'Celebratory-Item');
    const trophyAddress = questionnaire.contact.addresses!.find(address => address.addressTypeCode === 'Trophy');
    const role = winnersAndFinalistsPageUiStore.selectedWinner.role;

    try {
      if (questionnaire.questionnaireId < 0) {
        const questionnaireRef = await apiStore.createQuestionnaire(instance, winnersAndFinalistsPageUiStore.selectedWinner.winnerId, { ...questionnaire, answers: [], eventAttendees: [] });
        winnersAndFinalistsPageUiStore.setQuestionnaireId(questionnaire, questionnaireRef.questionnaireId);
        winnersAndFinalistsPageUiStore.setWinnerQuestionnaireId(questionnaire.organizationId, questionnaireRef.questionnaireId);

        const NACountry = countryStore.awardCountries.find(country => country.countryCode === 'N/A') ?? blankCountry();
        if (celebratoryItemAddress) {
          celebratoryItemAddress.questionnaireId = questionnaire.questionnaireId;
          if (celebratoryItemAddress.country.countryId <= 0) celebratoryItemAddress.country = NACountry;
        }
        if (trophyAddress) {
          trophyAddress.questionnaireId = questionnaire.questionnaireId;
          if (trophyAddress.country.countryId <= 0) trophyAddress.country = NACountry;
        }
      }

      const operations = [
        apiStore.updateWinnerContact(instance, questionnaire.questionnaireId, questionnaire.contact),
        apiStore.updateQuestionnaire(instance, questionnaire.questionnaireId, {
          mediaConsentDisclaimer: questionnaire.mediaConsentDisclaimer!
        }),
      ];

      if (celebratoryItemAddress && role !== 'Finalist') {
        operations.push(apiStore.updateWinnerAddress(instance, questionnaire.questionnaireId, 'Celebratory-Item', celebratoryItemAddress));
      }

      questionnaire.eventAttendees.forEach((eventAttendee) => {
        operations.push(
          (async () => {
            if (!eventAttendee.eventAttendeeId) {
              const ref = await apiStore.createEventAttendee(instance, questionnaire.questionnaireId, eventAttendee);
              eventAttendee.eventAttendeeId = ref.eventAttendeeId;
            } else {
              await apiStore.updateEventAttendee(instance, eventAttendee.eventAttendeeId, eventAttendee);
            }
          })()
        );
      });

      await Promise.all(operations);

    } catch (error) {
      // TODO - handle error
    } finally {
      if (winnersAndFinalistsPageUiStore.selectedAward) {
        await handleAwardSelected(winnersAndFinalistsPageUiStore.selectedAward);
      } else if (winnersAndFinalistsPageUiStore.selectedCountry) {
        await handleCountrySelected(winnersAndFinalistsPageUiStore.selectedCountry);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleCancelEditQuestionnaire = (questionnaireId: number): void => {
    winnersAndFinalistsPageUiStore.resetEditedQuestionnaire(questionnaireId);
  };

  const updateQuestionnaireMedia = async (questionnaireId: number): Promise<void> => {
    setIsLoading(true);
    try {
      const questionnaire = await apiStore.getQuestionnaire(instance, questionnaireId);
      const editedQuestionnaires = winnersAndFinalistsPageUiStore.getWinnerQuestionnaire(questionnaireId);
      editedQuestionnaires.logo = questionnaire.logo;
      editedQuestionnaires.media = questionnaire.media;
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUploadedMedia = async (questionnaireId: number, uploadedMediaId: number): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteUploadedMedia(instance, uploadedMediaId);
      await updateQuestionnaireMedia(questionnaireId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadMedia = async (questionnaireId: number, mediaContent: File, mediaType: QuestionnaireMediaType): Promise<void> => {
    setIsLoading(true);
    try {
      if (mediaType === 'logo') {
        await apiStore.uploadLogo(instance, questionnaireId, mediaContent);
      } else {
        await apiStore.uploadMedia(instance, questionnaireId, mediaContent);
      }
      await updateQuestionnaireMedia(questionnaireId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const download = async (req: ResourceTicketRequest): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, req);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const downloadUploadedMedia = async (uploadedMediaId: number): Promise<void> => {
    await download({
      data: {
        routeData: {
          uploadedMediaId: uploadedMediaId
        }
      },
      resourceType: ResourceTypes.UploadedMedia
    });
  }

  const handleRestoreMedia = async (questionnaireId: number, mediaType: QuestionnaireMediaType): Promise<void> => {
    setIsLoading(true);
    try {
      if (mediaType === 'logo') {
        await apiStore.restoreLogo(instance, questionnaireId);
      } else {
        await apiStore.restoreMedia(instance, questionnaireId);
      }
      await updateQuestionnaireMedia(questionnaireId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  };

  const loadQuestionnaire = async (): Promise<void> => {
    setIsLoading(true);
    if (!winnersAndFinalistsPageUiStore.selectedWinner) {
      setIsLoading(false);
      return;
    }

    const questions = await apiStore.getQuestionnaireQuestions(instance, winnersAndFinalistsPageUiStore.selectedWinner.winnerId)
    winnersAndFinalistsPageUiStore.setQuestions(questions)

    const getQuestionnaireTasks: Promise<Questionnaire>[] = [];
    const newQuestionnaires: Questionnaire[] = [];
    winnersAndFinalistsPageUiStore.selectedWinner.questionnaires.forEach(questionnaire => {
      if (questionnaire.questionnaireId && questionnaire.questionnaireId >= 0) getQuestionnaireTasks.push(apiStore.getQuestionnaire(instance, questionnaire.questionnaireId));
      else {
        const newQuestionnaire = blankQuestionnaire();
        const questionnaireId = -newQuestionnaires.length - 1;
        winnersAndFinalistsPageUiStore.setWinnerQuestionnaireId(questionnaire.organizationId, questionnaireId);
        newQuestionnaire.questionnaireId = questionnaireId;
        newQuestionnaire.organizationId = questionnaire.organizationId;
        newQuestionnaire.winnerId = winnersAndFinalistsPageUiStore.selectedWinner!.winnerId;
        newQuestionnaires.push(newQuestionnaire);
      }
    });
    await Promise.all(getQuestionnaireTasks)
      .then(questionnaires => {
        const existingQuestionnaires: Questionnaire[] = [...newQuestionnaires];
        questionnaires.forEach(questionnaire => {
          if (questionnaire) existingQuestionnaires.push(questionnaire);
        });
        winnersAndFinalistsPageUiStore.setQuestionnaires(existingQuestionnaires);
      });

    setIsLoading(false);
  };

  const handleTabChange = async (tab: WinnersAndFinalistPageTabs): Promise<void> => {
    setActiveTab(tab);
    winnersAndFinalistsPageUiStore.setWinnerOnTabChange(tab);
    await loadQuestionnaire();
  };

  const renderTabs = (): React.JSX.Element => {
    const isAwardNotSelected = !winnersAndFinalistsPageUiStore.selectedAward && !winnersAndFinalistsPageUiStore.selectedCountry;
    const isCountryAward = winnersAndFinalistsPageUiStore.selectedCountry !== undefined;
    return (
      <TabList
        className={mergeClasses(classes.root, 'winners-and-finalists-page__tabs')}
        selectedValue={activeTab ?? ''}
        onTabSelect={(_, tabData) => handleTabChange(tabData.value as WinnersAndFinalistPageTabs)}
      >
        <Tab
          value='Winner'
          disabled={isAwardNotSelected}
        >
          Winner
        </Tab>
        {
          !isCountryAward && (
            <>
              <Text weight='bold'>|</Text>
              <Tab
                value='Finalist 1'
                disabled={isAwardNotSelected}
              >
                Finalist 1
              </Tab>
              <Text weight='bold'>|</Text>
              <Tab
                value='Finalist 2'
                disabled={isAwardNotSelected}
              >
                Finalist 2
              </Tab>
              <Text weight='bold'>|</Text>
              <Tab
                value='Finalist 3'
                disabled={isAwardNotSelected}
              >
                Finalist 3
              </Tab>
            </>
          )
        }
      </TabList>
    );
  };

  const renderWinnerDetails = (winnerQuestionnaire: WinnerQuestionnaire) => {
    return (
      <WinnerDetails
        organizationName={winnerQuestionnaire.organizationName}
        questionnaireId={winnerQuestionnaire.questionnaireId}
        onDownloadUploadedMedia={(uploadedMediaId) => downloadUploadedMedia(uploadedMediaId)}
        onDeleteUploadedMedia={handleDeleteUploadedMedia}
        onRestoreMedia={handleRestoreMedia}
        onUploadMedia={handleUploadMedia}
        onSaveQuestionnaire={handleSaveEditedQuestionnaire}
      />
    );
  };

  const renderWinnerDetailsWithAccordion = () => {
    if (!winnersAndFinalistsPageUiStore.selectedWinner) return;
    return (
      <Accordion
        collapsible
        className='winner-details-with-accordion'
        defaultOpenItems={1}>
        {
          winnersAndFinalistsPageUiStore.selectedWinner.questionnaires.map((winnerQuestionnaire, index) => {
            return (
              <AccordionItem
                key={`winner-finalist-${index + 1}`}
                value={`winner-finalist-${index + 1}`}
              >
                <AccordionHeader>
                  <Text weight='semibold'>
                    {`${winnerQuestionnaire.organizationName} (${winnerQuestionnaire.organizationType})`}
                  </Text>
                </AccordionHeader>
                <AccordionPanel>
                  {renderWinnerDetails(winnerQuestionnaire)}
                </AccordionPanel>
              </AccordionItem>
            );
          })
        }
      </Accordion>
    )
  };

  const getStaffProgramWinnerRegion = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const regions = await apiStore.getStaffProgramWinnerRegions(instance, awardStore.defaultProgramRef.programId);
      const countries = regions.flatMap((region) => region.countries);
      winnersAndFinalistsPageUiStore.setWinnerCountries(countries);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getAdminAwardGroups = async (includeCountryAward: boolean): Promise<void> => {
    try {
      const awardGroups = await apiStore.getAdminAwardGroups(instance, { includeCountryAward });
      winnersAndFinalistsPageUiStore.setAwardGroups(awardGroups);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getWinnerEvents = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const winnerEvents = await apiStore.getWinnerEvents(instance, awardStore.defaultProgramRef.programId);
      winnersAndFinalistsPageUiStore.setWinnerEvents(winnerEvents);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    await Promise.allSettled([
      getStaffProgramWinnerRegion(),
      getAdminAwardGroups(true),
      getWinnerEvents(),
    ]);

    setIsLoading(false);
  };

  useEffect(() => {
    preloadData();
    return () => {
      winnersAndFinalistsPageUiStore.resetStates();
    }
  }, []);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      className='winners-and-finalists-page'
    >
      {
        (isLoading || isDownloadServiceInFlight) && <Loading />
      }
      <FlexBox
        isFullWidth
        content='space-between'
        aligns='end'
        className='winners-and-finalists-page__header'
      >
        <PageTitle
          title='Winners & Finalists'
          subtitle={awardStore.defaultProgramRef?.displayName}
        />
        <FlexBox
          direction='column'
          aligns='end'
          className='header-tabs'
        >
          <Button
            appearance='primary'
            disabled
          >
            Download All Media
          </Button>
          {renderTabs()}
        </FlexBox>
      </FlexBox>
      <FlexBox
        isFullWidth
        className='winners-and-finalists-page__main'
      >
        <div className='main-sidebar'>
          <div className='main-sidebar__search-box'>
            <SearchBox
              id='winners-finalists-search'
              placeholder='Search'
              value={searchText}
              className={mergeClasses(commonStyles.inputField, 'search-box__input')}
              onChange={(_, data) => handleSearchTextChanged(data.value)}
            />
          </div>
          <AwardGroupList
            onAwardSelected={(award) => handleAwardSelected(award)}
            onCountrySelected={(country) => handleCountrySelected(country)}
          />
        </div>
        <div className='main-content'>
          {
            winnersAndFinalistsPageUiStore.selectedAward || winnersAndFinalistsPageUiStore.selectedCountry ?
              <div>
                {
                  winnersAndFinalistsPageUiStore.selectedWinner && winnersAndFinalistsPageUiStore.selectedWinner.questionnaires?.length > 0 ?
                    winnersAndFinalistsPageUiStore.selectedWinner.questionnaires.length > 1 ?
                      renderWinnerDetailsWithAccordion() :
                      renderWinnerDetails(winnersAndFinalistsPageUiStore.selectedWinner.questionnaires[0]) :
                    <Text
                      as='p'
                      block
                      size={500}
                      className='main-content__fallback'
                    >
                      No nomination designated yet
                    </Text>
                }
              </div> :
              <Text
                as='p'
                block
                size={500}
                className='main-content__fallback'
              >
                Select an award to view winners and finalists.
              </Text>
          }
        </div>
      </FlexBox>
    </FlexBox>
  );
}

export const WinnersAndFinalistsPage = observer(WinnersAndFinalistsPageImpl);
